import { PrepaidPurchaseStatus } from './prepaid-wash-book.models';

/** Redeemer contact/vehicle fields (may differ from booking purchaser). */
export interface PrepaidRedemptionRecipientForm {
  licensePlate: string;
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  customerId?: string;
  vehicleId?: string;
}

export interface PrepaidRedemptionPurchaserSnapshot {
  licensePlate: string;
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  customerId: string;
  vehicleId: string;
}

export type PrepaidRedemptionResolveAction =
  | 'reuse_purchaser'
  | 'update_customer'
  | 'create_customer'
  | 'add_vehicle'
  | 'resolve_different_customer';

export type PrepaidRedemptionContactField = 'phone' | 'email' | 'firstName' | 'lastName';

export function emptyPrepaidRedemptionRecipient(): PrepaidRedemptionRecipientForm {
  return {
    licensePlate: '',
    phone: '',
    firstName: '',
    lastName: '',
    email: '',
  };
}

export function normalizePlateForRecipientCompare(plate: string): string {
  return String(plate ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toUpperCase();
}

export function normalizePhoneForRecipientCompare(phone: string): string {
  return String(phone ?? '').replace(/\D/g, '');
}

export function normalizeEmailForRecipientCompare(email: string): string {
  return String(email ?? '')
    .trim()
    .toLowerCase();
}

/** Same person as purchaser: matching plate or customer id (contact edits do not change identity). */
export function isPrepaidRedemptionSameCustomerIdentity(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  const purchaserPlate = normalizePlateForRecipientCompare(purchaser.licensePlate);
  const recipientPlate = normalizePlateForRecipientCompare(recipient.licensePlate);
  if (purchaserPlate && recipientPlate && purchaserPlate === recipientPlate) {
    return true;
  }

  const purchaserCustomerId = (purchaser.customerId || '').trim();
  const recipientCustomerId = (recipient.customerId || '').trim();
  if (purchaserCustomerId && recipientCustomerId && purchaserCustomerId === recipientCustomerId) {
    return true;
  }

  return false;
}

/** Both purchaser and recipient have phone/email filled and values differ → new customer record. */
export function prepaidRedemptionPhoneOrEmailChangedRequiringNewCustomer(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  const purchaserPhone = normalizePhoneForRecipientCompare(purchaser.phone);
  const recipientPhone = normalizePhoneForRecipientCompare(recipient.phone);
  if (purchaserPhone && recipientPhone && purchaserPhone !== recipientPhone) {
    return true;
  }

  const purchaserEmail = normalizeEmailForRecipientCompare(purchaser.email);
  const recipientEmail = normalizeEmailForRecipientCompare(recipient.email);
  if (purchaserEmail && recipientEmail && purchaserEmail !== recipientEmail) {
    return true;
  }

  return false;
}

function prepaidRedemptionPhoneOrEmailDiffers(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  const purchaserPhone = normalizePhoneForRecipientCompare(purchaser.phone);
  const recipientPhone = normalizePhoneForRecipientCompare(recipient.phone);
  if (purchaserPhone !== recipientPhone) {
    return true;
  }

  const purchaserEmail = normalizeEmailForRecipientCompare(purchaser.email);
  const recipientEmail = normalizeEmailForRecipientCompare(recipient.email);
  return purchaserEmail !== recipientEmail;
}

/** Name/phone/email differs from purchaser, including filling previously empty fields. */
export function prepaidRedemptionContactFieldsNeedUpdate(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  return (
    prepaidRedemptionNameChanged(recipient, purchaser) ||
    prepaidRedemptionPhoneOrEmailDiffers(recipient, purchaser)
  );
}

export function prepaidRedemptionNameChanged(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  const purchaserFirst = (purchaser.firstName || '').trim();
  const recipientFirst = (recipient.firstName || '').trim();
  const purchaserLast = (purchaser.lastName || '').trim();
  const recipientLast = (recipient.lastName || '').trim();
  return purchaserFirst !== recipientFirst || purchaserLast !== recipientLast;
}

export function prepaidRedemptionVehiclePlateChanged(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  const purchaserPlate = normalizePlateForRecipientCompare(purchaser.licensePlate);
  const recipientPlate = normalizePlateForRecipientCompare(recipient.licensePlate);
  return !!(purchaserPlate && recipientPlate && purchaserPlate !== recipientPlate);
}

/** True when a redeemer contact field differs from the immutable prefilled snapshot. */
export function prepaidRedemptionContactFieldOverwritten(
  recipient: PrepaidRedemptionRecipientForm,
  prefilled: PrepaidRedemptionPurchaserSnapshot,
  field: PrepaidRedemptionContactField
): boolean {
  switch (field) {
    case 'phone':
      return (
        normalizePhoneForRecipientCompare(recipient.phone) !==
        normalizePhoneForRecipientCompare(prefilled.phone)
      );
    case 'email':
      return (
        normalizeEmailForRecipientCompare(recipient.email) !==
        normalizeEmailForRecipientCompare(prefilled.email)
      );
    case 'firstName':
      return (recipient.firstName || '').trim() !== (prefilled.firstName || '').trim();
    case 'lastName':
      return (recipient.lastName || '').trim() !== (prefilled.lastName || '').trim();
  }
}

/** True when any phone / email / first name / last name was overwritten from prefilled. */
export function prepaidRedemptionContactOverwritten(
  recipient: PrepaidRedemptionRecipientForm,
  prefilled: PrepaidRedemptionPurchaserSnapshot
): boolean {
  return (
    prepaidRedemptionContactFieldOverwritten(recipient, prefilled, 'phone') ||
    prepaidRedemptionContactFieldOverwritten(recipient, prefilled, 'email') ||
    prepaidRedemptionContactFieldOverwritten(recipient, prefilled, 'firstName') ||
    prepaidRedemptionContactFieldOverwritten(recipient, prefilled, 'lastName')
  );
}

/** Prefilled snapshot when captured; otherwise live form + purchase status. */
export function getPrepaidRedemptionPurchaserBaseline(
  prefilledSnapshot: PrepaidRedemptionPurchaserSnapshot | null,
  formData: {
    vehicleLicensePlate?: string;
    customerContact?: string;
    customerFirstName?: string;
    customerLastName?: string;
    customerEmail?: string;
    customerId?: string;
    vehicleId?: string;
  },
  status?: PrepaidPurchaseStatus | null
): PrepaidRedemptionPurchaserSnapshot {
  if (prefilledSnapshot) {
    return prefilledSnapshot;
  }
  return buildPurchaserSnapshotFromFormData(formData, status);
}

/** Determines customer/vehicle API work needed before prepaid redeem submit. */
export function resolvePrepaidRedemptionAction(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot,
  prefilledSnapshot?: PrepaidRedemptionPurchaserSnapshot | null
): PrepaidRedemptionResolveAction {
  const baseline = prefilledSnapshot ?? purchaser;
  // CWS-1914: there is no customer on either side — a plate scanned at the lane that nobody
  // has ever bought anything under. Reusing or updating would both need a record that does
  // not exist, which is what refused the redemption outright. Resolve the redeemer on their
  // own terms instead: matched on phone, then email, created only if they shared a phone.
  if (!(baseline.customerId || '').trim() && !(recipient.customerId || '').trim()) {
    return 'create_customer';
  }
  if (!isPrepaidRedemptionSameCustomerIdentity(recipient, baseline)) {
    return 'resolve_different_customer';
  }
  // CWS-1881: a phone or an email replaced with a different one is a different person, however
  // much of the purchaser's prefill the form is still carrying. This used to fall through to
  // 'update_customer', which wrote the walk-in's details onto the record of whoever bought the
  // book. Their own record is matched on phone, then email, before anything is created.
  if (prepaidRedemptionPhoneOrEmailChangedRequiringNewCustomer(recipient, baseline)) {
    return 'create_customer';
  }
  if (prepaidRedemptionContactFieldsNeedUpdate(recipient, baseline)) {
    return 'update_customer';
  }
  if (prepaidRedemptionVehiclePlateChanged(recipient, baseline)) {
    return 'add_vehicle';
  }
  return 'reuse_purchaser';
}

/**
 * CWS-1881: the redeemer pane opens prefilled from the purchaser — `customerId` and
 * `vehicleId` included, not just the visible fields. Once the attendant has typed a different
 * plate or a different phone, those ids belong to someone who is not standing there, and
 * `isPrepaidRedemptionSameCustomerIdentity` reads the leftover id as "same person". That is
 * how a walk-in redeeming a relative's book ended up renaming the purchaser.
 *
 * The vehicle id is kept while the plate is untouched: it still names that car.
 */
export function dropStalePurchaserIdsFromRecipient(
  recipient: PrepaidRedemptionRecipientForm,
  prefilled: PrepaidRedemptionPurchaserSnapshot | null | undefined
): PrepaidRedemptionRecipientForm {
  const prefilledCustomerId = (prefilled?.customerId || '').trim();
  const recipientCustomerId = (recipient.customerId || '').trim();
  if (!prefilledCustomerId || prefilledCustomerId !== recipientCustomerId) {
    return recipient;
  }

  const plateChanged =
    normalizePlateForRecipientCompare(recipient.licensePlate) !==
    normalizePlateForRecipientCompare(prefilled?.licensePlate ?? '');
  const phoneChanged =
    normalizePhoneForRecipientCompare(recipient.phone) !==
    normalizePhoneForRecipientCompare(prefilled?.phone ?? '');
  if (!plateChanged && !phoneChanged) {
    return recipient;
  }

  return {
    ...recipient,
    customerId: undefined,
    vehicleId: plateChanged ? undefined : recipient.vehicleId,
  };
}

/** @deprecated Use resolvePrepaidRedemptionAction(...) === 'reuse_purchaser' */
export function prepaidRedemptionRecipientMatchesPurchaser(
  recipient: PrepaidRedemptionRecipientForm,
  purchaser: PrepaidRedemptionPurchaserSnapshot
): boolean {
  return resolvePrepaidRedemptionAction(recipient, purchaser) === 'reuse_purchaser';
}

/** Snapshot booking purchaser fields for default redeemer pane. */
export function buildPrepaidRedemptionRecipientFromPurchaser(
  purchaser: PrepaidRedemptionPurchaserSnapshot
): PrepaidRedemptionRecipientForm {
  return {
    licensePlate: purchaser.licensePlate?.trim() ?? '',
    phone: purchaser.phone?.trim() ?? '',
    firstName: purchaser.firstName?.trim() ?? '',
    lastName: purchaser.lastName?.trim() ?? '',
    email: purchaser.email?.trim() ?? '',
    customerId: purchaser.customerId?.trim() || undefined,
    vehicleId: purchaser.vehicleId?.trim() || undefined,
  };
}

export function buildPurchaserSnapshotFromFormData(
  formData: {
    vehicleLicensePlate?: string;
    customerContact?: string;
    customerFirstName?: string;
    customerLastName?: string;
    customerEmail?: string;
    customerId?: string;
    vehicleId?: string;
  },
  status?: PrepaidPurchaseStatus | null
): PrepaidRedemptionPurchaserSnapshot {
  const vehicleId = (formData.vehicleId || '').trim() || (status?.purchase_vehicle_id || '').trim();
  const customerId = (formData.customerId || '').trim() || (status?.customer_id || '').trim();
  return {
    licensePlate: formData.vehicleLicensePlate?.trim() ?? '',
    phone: formData.customerContact?.trim() ?? '',
    firstName: formData.customerFirstName?.trim() ?? '',
    lastName: formData.customerLastName?.trim() ?? '',
    email: formData.customerEmail?.trim() ?? '',
    customerId,
    vehicleId,
  };
}

/** The subset of a customer record the redemption panel could fill in. */
export interface RedemptionCustomerPatch {
  first_name?: string;
  last_name?: string;
  email?: string;
  contact?: string;
}

/**
 * CWS-1881: what may safely be written to a customer the redeemer's phone matched.
 *
 * The redemption panel used to PUT the whole form onto that customer, so a mistyped digit
 * that happened to match a real customer silently renamed them and replaced their email.
 * A lane is the worst possible place to let that happen: the attendant is typing fast, from
 * someone else's dictation, about a person who is not in front of them.
 *
 * Only genuinely empty fields are filled, so an existing record can gain an email it never
 * had but can never lose one it already has. Whatever was typed is still kept verbatim on
 * the redemption row, so nothing the attendant captured is lost — and the design's own
 * instruction for changing a customer is to open their record through the row's link.
 *
 * @returns the patch to send, or null when the existing record already has everything.
 */
export function buildBlankOnlyCustomerPatch(
  existing: Record<string, unknown> | null | undefined,
  recipient: PrepaidRedemptionRecipientForm
): RedemptionCustomerPatch | null {
  // Unknown record: write nothing rather than guess what is safe to overwrite.
  if (!existing) {
    return null;
  }

  const isBlank = (value: unknown): boolean => String(value ?? '').trim() === '';
  const patch: RedemptionCustomerPatch = {};

  const fill = (
    column: keyof RedemptionCustomerPatch,
    existingKey: string,
    typed: string | undefined
  ): void => {
    const value = String(typed ?? '').trim();
    if (value && isBlank(existing[existingKey])) {
      patch[column] = value;
    }
  };

  fill('first_name', 'first_name', recipient.firstName);
  fill('last_name', 'last_name', recipient.lastName);
  fill('email', 'email', recipient.email);
  fill('contact', 'contact', recipient.phone);

  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * CWS-1914: a customer record with nothing on it but an id.
 *
 * A plate read at the lane creates one of these so the car has somewhere to live before
 * anyone has been identified — the "Unknown Customer" in the customer list. It is a
 * placeholder, not a person, so moving its car to the customer the attendant actually
 * identified loses nobody anything.
 *
 * A record with so much as a name is a real customer and is never moved: that refusal is
 * the whole point of VEHICLE_PLATE_TAKEN.
 */
export function isPlaceholderCustomerRecord(
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record) {
    return false;
  }
  const filled = (key: string): boolean => String(record[key] ?? '').trim() !== '';
  return !filled('first_name') && !filled('last_name') && !filled('contact') && !filled('email');
}

/** The `redeemer_*` fields a prepaid redemption request carries (svc-order validator, CWS-1881). */
export interface PrepaidRedeemerPayloadFields {
  redeemer_customer_id?: string;
  redeemer_first_name?: string;
  redeemer_last_name?: string;
  redeemer_phone?: string;
  redeemer_email?: string;
  redeemer_plate?: string;
}

/**
 * CWS-1881: what the redemption row records about who came in. The request never carried
 * these, so a redeemer who gave a name or an email but no phone, the case the design keeps
 * off the customer table on purpose, was written as nothing at all. Every non-blank field
 * is sent; the customer id only when a phone was shared, since that is the one case a
 * record of the redeemer's own exists (matched or created before this request).
 */
export function buildPrepaidRedeemerPayloadFields(
  recipient: PrepaidRedemptionRecipientForm,
  resolvedCustomerId: string | null | undefined
): PrepaidRedeemerPayloadFields {
  const out: PrepaidRedeemerPayloadFields = {};
  const put = (key: keyof PrepaidRedeemerPayloadFields, value: string | undefined): void => {
    const trimmed = String(value ?? '').trim();
    if (trimmed) {
      out[key] = trimmed;
    }
  };
  put('redeemer_plate', recipient.licensePlate);
  put('redeemer_first_name', recipient.firstName);
  put('redeemer_last_name', recipient.lastName);
  put('redeemer_phone', recipient.phone);
  put('redeemer_email', recipient.email);
  if (out.redeemer_phone) {
    put('redeemer_customer_id', resolvedCustomerId ?? undefined);
  }
  return out;
}
